// -----------------------------------------------------------------------------
// OWProfile — thin wrapper over the injected overwolf profile APIs the store
// needs, shared by the website (browser / embedded iframe) and the ow-app in-app
// login (native extension page). Both see the client through window.overwolf, so
// this reads it directly through a minimal local type — neither package shares
// the full @overwolf/types ambient namespace.
// -----------------------------------------------------------------------------

// The signed-in user as returned by overwolf.profile.getCurrentUser. The client
// returns more fields; only the ones the store consumes are typed here. Notably
// there is no plaintext email — only emailHashes.
export interface OverwolfProfileResult {
  status: string;
  success: boolean;
  username: string;
  displayName: string;
  userId: string;
  avatar: string;
  // Device identifiers, present regardless of login state. Used for analytics
  // (MUID / MUIDV2) — see getMachineInfo.
  machineId?: string;
  machineIdV2?: string;
  emailHashes?: {
    md5: string;
    sha1: string;
    sha256: string;
  };
}

// Device identifiers, read regardless of login success (device-level, not
// account-level) so analytics can attribute events even when logged out.
export interface OverwolfMachineInfo {
  machineId: string | null;
  machineIdV2: string | null;
}

type LoginStateListener = (state: unknown) => void;

interface OverwolfProfileApi {
  getCurrentUser: (callback: (result: OverwolfProfileResult) => void) => void;
  onLoginStateChanged?: {
    addListener: (listener: LoginStateListener) => void;
    removeListener: (listener: LoginStateListener) => void;
  };
}

interface OverwolfGlobal {
  version: string;
  profile?: OverwolfProfileApi;
}

// -----------------------------------------------------------------------------
const getOverwolf = (): OverwolfGlobal | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return (window as unknown as { overwolf?: OverwolfGlobal }).overwolf;
};

// -----------------------------------------------------------------------------
export class OWProfile {
  // ---------------------------------------------------------------------------
  /** True when the injected overwolf client object is present. */
  public static isAvailable(): boolean {
    return getOverwolf() !== undefined;
  }

  // ---------------------------------------------------------------------------
  /** The Overwolf client version, or null off-client. */
  public static getVersion(): string | null {
    return getOverwolf()?.version ?? null;
  }

  // ---------------------------------------------------------------------------
  // Reads the current client user via overwolf.profile.getCurrentUser (a
  // callback API, wrapped in a Promise). Resolves the raw result, or null when
  // the API is unavailable or errors. Callers decide whether to filter on
  // `success`.
  public static getCurrentUser(): Promise<OverwolfProfileResult | null> {
    const profile = getOverwolf()?.profile;
    if (!profile?.getCurrentUser) {
      return Promise.resolve(null);
    }

    return new Promise<OverwolfProfileResult | null>((resolve) => {
      try {
        profile.getCurrentUser((result) => resolve(result ?? null));
      } catch {
        resolve(null);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // The device identifiers the client reports on getCurrentUser. Resolves nulls
  // when the API is unavailable or errors.
  public static async getMachineInfo(): Promise<OverwolfMachineInfo> {
    const result = await this.getCurrentUser();
    return {
      machineId: result?.machineId ?? null,
      machineIdV2: result?.machineIdV2 ?? null,
    };
  }

  // ---------------------------------------------------------------------------
  // Subscribes to client login-state changes so callers can re-read the profile
  // after sign in / out. Returns an unsubscribe function; a no-op when the API
  // isn't available.
  public static onLoginStateChanged(listener: () => void): () => void {
    const onLoginStateChanged = getOverwolf()?.profile?.onLoginStateChanged;
    if (!onLoginStateChanged) {
      return () => undefined;
    }

    const wrapped: LoginStateListener = () => listener();
    onLoginStateChanged.addListener(wrapped);

    return () => {
      onLoginStateChanged.removeListener(wrapped);
    };
  }
}
